// =====================================================
// BUDGET FORM PAGE - Formulario completo de presupuesto
// Con tratamiento, plantillas, odontograma, sociedades
// =====================================================
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  Loader2,
  User,
  FileText,
  Plus,
  Calculator,
  AlertCircle,
  FileStack,
  Tag,
  Send,
  Mail,
  Printer,
  X,
  Trash2,
  CreditCard,
} from 'lucide-react';
import { budgetsApi } from '../../api/budgets';
import { patientsApi } from '../../api/patients';
import { discountsApi, type DiscountSelect } from '../../api/discounts';
import { PatientSelectModal } from '../../components/budgets/PatientSelectModal';
import { MedicalActSearch } from '../../components/budgets/MedicalActSearch';
import { TemplateLoaderModal } from '../../components/budgets/TemplateLoaderModal';
import { TreatmentSelector } from '../../components/budgets/TreatmentSelector';
import { BudgetLineEditor, type EditableLine } from '../../components/budgets/BudgetLineEditor';
import { BudgetSummary } from '../../components/budgets/BudgetSummary';
import { OdontogramModal, type OdontogramSelection, type OdontogramType } from '../../components/odontogram';
import type {
  BudgetResponse,
  BudgetMedicalActInfo,
  CreateBudgetRequest,
} from '../../types/budget';
import type { Patient } from '../../types/patient';
import type { TreatmentSelect } from '../../api/treatments';
import type { TemplatePreview } from '../../api/templates';
import { useAuth } from '../../contexts/AuthContext';
import { formatCurrency } from '../../utils/format';
import { useTrackUnsavedChanges } from '../../contexts/UnsavedChangesContext';

export function BudgetFormPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const returnPatientId = searchParams.get('patientId');
  const { activeSite } = useAuth();

  const isEditing = !!id;

  // Ref para acceso a availableDiscounts dentro de funciones async (evita stale closure)
  const availableDiscountsRef = useRef<DiscountSelect[]>([]);

  // Estado principal
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Datos del presupuesto
  const [budget, setBudget] = useState<BudgetResponse | null>(null);
  const [budgetId, setBudgetId] = useState<string | null>(id || null);
  const [patient, setPatient] = useState<Patient | null>(null);
  const [treatment, setTreatment] = useState<TreatmentSelect | null>(null);
  const [lines, setLines] = useState<EditableLine[]>([]);
  const [internalNotes, setInternalNotes] = useState('');
  const [publicNotes, setPublicNotes] = useState('');
  const [globalDiscountType, setGlobalDiscountType] = useState<'PERCENTAGE' | 'FIXED' | null>(null);
  const [globalDiscountValue, setGlobalDiscountValue] = useState<number | null>(null);
  const [globalDiscountId, setGlobalDiscountId] = useState<string | null>(null);
  const [globalDiscountName, setGlobalDiscountName] = useState<string | null>(null);
  const [availableDiscounts, setAvailableDiscounts] = useState<DiscountSelect[]>([]);

  // Modales
  const [showPatientModal, setShowPatientModal] = useState(false);
  const [showMedicalActSearch, setShowMedicalActSearch] = useState(false);
  const [showTreatmentRequiredModal, setShowTreatmentRequiredModal] = useState(false);
  const [showTemplateLoader, setShowTemplateLoader] = useState(false);
  const [showOdontogram, setShowOdontogram] = useState(false);
  const [currentOdontogramLineId, setCurrentOdontogramLineId] = useState<string | null>(null);

  // Modal: eliminar presupuesto
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Modal: enviar presupuesto
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [isSending, setIsSending] = useState(false);

  // Sociedad por defecto del centro
  const defaultSocietyId = activeSite?.defaultSocietyId || null;

  // =====================================================
  // DIRTY TRACKING - Detección de cambios sin guardar
  // =====================================================
  const [showExitConfirmModal, setShowExitConfirmModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);
  const initialStateRef = useRef<string | null>(null);
  const hasInitialized = useRef(false);
  const [pendingReset, setPendingReset] = useState(false);

  // Serializar estado actual para comparación
  const currentStateSnapshot = useMemo(() => {
    return JSON.stringify({
      patientId: patient?.id || null,
      treatmentId: treatment?.id || null,
      lines: lines
        .filter(l => !l._deleted)
        .map(l => ({
          id: l.id,
          medicalActId: l.medicalActId,
          quantity: l.quantity,
          unitPrice: l.unitPrice,
          discountType: l.discountType,
          discountValue: l.discountValue,
          societyId: l.societyId,
          tariffId: l.tariffId,
          odontogramSelection: l.odontogramSelection?.displayText || null,
        })),
      internalNotes,
      publicNotes,
      globalDiscountType,
      globalDiscountValue,
    });
  }, [patient, treatment, lines, internalNotes, publicNotes, globalDiscountType, globalDiscountValue]);

  // Guardar snapshot inicial cuando se carga el presupuesto o se crea uno nuevo
  useEffect(() => {
    if (!hasInitialized.current && !isLoading) {
      // Pequeño delay para que se estabilice el estado inicial
      const timer = setTimeout(() => {
        initialStateRef.current = currentStateSnapshot;
        hasInitialized.current = true;
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isLoading, currentStateSnapshot]);

  // Resetear snapshot después de guardar exitosamente
  const resetDirtyState = useCallback(() => {
    setPendingReset(true);
  }, []);

  // Efecto para resetear dirty state después de que React actualice
  useEffect(() => {
    if (pendingReset) {
      initialStateRef.current = currentStateSnapshot;
      setPendingReset(false);
    }
  }, [pendingReset, currentStateSnapshot]);

  // Determinar si hay cambios sin guardar
  const isDirty = useMemo(() => {
    if (!hasInitialized.current || initialStateRef.current === null) {
      return false;
    }
    return currentStateSnapshot !== initialStateRef.current;
  }, [pendingReset, currentStateSnapshot]);
  // Sincronizar con contexto global para interceptar navegación del Sidebar
  useTrackUnsavedChanges(isDirty, "Presupuesto");


  const handleDelete = async () => {
    if (!budgetId) return;
    setIsDeleting(true);
    try {
      await budgetsApi.delete(budgetId);
      navigate('/presupuestos');
    } catch (err: any) {
      setError(err.message || 'Error al eliminar el presupuesto');
      setShowDeleteModal(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleOpenSend = async () => {
    if (!budgetId) return;
    if (isDirty) { await handleSave(false); }
    setEmailTo(patient?.email || '');
    setSendEmail(false);
    setShowSendModal(true);
  };

  const handleConfirmSend = async () => {
    if (!budgetId) return;
    setIsSending(true);
    try {
      await budgetsApi.send(budgetId, {
        sendEmail,
        email: sendEmail && emailTo ? emailTo : undefined,
        printOnly: !sendEmail,
      });
      setShowSendModal(false);
      navigate(`/presupuestos/${budgetId}`);
    } catch (err: any) {
      setError(err.message || 'Error al enviar el presupuesto');
      setShowSendModal(false);
    } finally {
      setIsSending(false);
    }
  };

  // Navegación segura que verifica cambios pendientes
  const safeNavigate = useCallback((path: string) => {
    if (isDirty) {
      setPendingNavigation(() => () => navigate(path));
      setShowExitConfirmModal(true);
    } else {
      navigate(path);
    }
  }, [isDirty, navigate]);

  // Prevenir cierre de pestaña/navegador
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);


  // Cargar paciente si viene en URL
  useEffect(() => {
    if (returnPatientId && !patient) {
      loadPatient(returnPatientId);
    } else if (!returnPatientId && !patient && !isEditing) {
      setShowPatientModal(true);
    }
  }, [returnPatientId]);

  // Cargar presupuesto si estamos editando
  useEffect(() => {
    if (isEditing && id) {
      loadBudget(id);
    }
  }, [isEditing, id]);

  // Cargar descuentos activos vinculados al centro
  useEffect(() => {
    if (activeSite?.id) {
      discountsApi.getForSelectBySite(activeSite.id)
        .then(discounts => {
          availableDiscountsRef.current = discounts;
          setAvailableDiscounts(discounts);
        })
        .catch(() => {
          availableDiscountsRef.current = [];
          setAvailableDiscounts([]);
        });
    }
  }, [activeSite?.id]);

  // Reconciliar discountId/Name cuando los descuentos cargan DESPUÉS del presupuesto.
  // El guard "needsReconciliation" evita bucles infinitos: si ya todo está reconciliado,
  // el updater devuelve la misma referencia y React no re-renderiza.
  useEffect(() => {
    if (availableDiscounts.length === 0) return;
    setLines(prev => {
      const needsReconciliation = prev.some(
        l => !l.discountId && !!l.discountType && !!l.discountValue
      );
      if (!needsReconciliation) return prev;
      return prev.map(line => {
        if (line.discountId) return line;
        if (!line.discountType || !line.discountValue) return line;
        const match = availableDiscounts.find(
          d => d.type === line.discountType && Number(d.value) === Number(line.discountValue)
        );
        return match ? { ...line, discountId: match.id, discountName: match.name } : line;
      });
    });
    // Reconciliar descuento global si aún no tiene id
    setGlobalDiscountId(prev => {
      if (prev) return prev;
      if (!globalDiscountType || !globalDiscountValue) return prev;
      return availableDiscounts.find(
        d => d.type === globalDiscountType && Number(d.value) === Number(globalDiscountValue)
      )?.id || prev;
    });
    setGlobalDiscountName(prev => {
      if (prev) return prev;
      if (!globalDiscountType || !globalDiscountValue) return prev;
      return availableDiscounts.find(
        d => d.type === globalDiscountType && Number(d.value) === Number(globalDiscountValue)
      )?.name || prev;
    });
  }, [availableDiscounts]);

  const loadPatient = async (patientId: string) => {
    try {
      const data = await patientsApi.getById(patientId);
      setPatient(data);
    } catch (err: any) {
      setError('Error al cargar los datos del paciente');
    }
  };

  const loadBudget = async (budgetId: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await budgetsApi.getById(budgetId);
      setBudget(data);
      
      await loadPatient(data.patient.id);
      
      if (data.treatment) {
        setTreatment({
          id: data.treatment.id,
          code: data.treatment.code,
          name: data.treatment.name,
        });
      }
      
      const editableLines: EditableLine[] = data.lines.map((line) => ({
        tempId: `existing-${line.id}`,
        id: line.id,
        medicalActId: line.medicalActId || '',
        medicalActCode: line.medicalActCode,
        medicalActName: line.medicalActName,
        requiresOdontogram: line.requiresOdontogram,
        odontogramType: line.odontogramType,
        quantity: line.quantity,
        unitPrice: line.unitPrice,
        priceSource: line.priceSource,
        discountType: line.discountType as 'PERCENTAGE' | 'FIXED' | null,
        discountValue: line.discountValue,
        // discountId/discountName se reconcilian en el useEffect de availableDiscounts
        discountId: null,
        discountName: null,
        odontogramSelection: line.toothNumbers ? parseOdontogramFromString(line.toothNumbers) : null,
        societyId: line.society?.id || null,
        societyName: line.society?.name || null,
        tariffId: line.tariff?.id || null,
        tariffName: line.tariff?.name || null,
        // siteSocieties viene en la respuesta del backend (findById lo incluye)
        availableSocieties: data.siteSocieties || [],
      }));

      // Reconciliar discountId/Name usando el ref (funciona aunque los descuentos
      // ya estuvieran cargados antes que el presupuesto — evita la race condition)
      const reconciledLines = editableLines.map(line => {
        if (!line.discountType || !line.discountValue) return line;
        const match = availableDiscountsRef.current.find(
          d => d.type === line.discountType && Number(d.value) === Number(line.discountValue)
        );
        return match ? { ...line, discountId: match.id, discountName: match.name } : line;
      });
      setLines(reconciledLines);
      
      setInternalNotes(data.internalNotes || '');
      setPublicNotes(data.publicNotes || '');
      setGlobalDiscountType(data.discountType as 'PERCENTAGE' | 'FIXED' | null);
      setGlobalDiscountValue(data.discountValue);

      // Reconciliar descuento global
      if (data.discountType && data.discountValue) {
        const globalMatch = availableDiscountsRef.current.find(
          d => d.type === data.discountType && Number(d.value) === Number(data.discountValue)
        );
        setGlobalDiscountId(globalMatch?.id || null);
        setGlobalDiscountName(globalMatch?.name || null);
      }
    } catch (err: any) {
      setError(err.message || 'Error al cargar el presupuesto');
    } finally {
      setIsLoading(false);
    }
  };

  // Parsear string de dientes a OdontogramSelection
  const parseOdontogramFromString = (str: string): OdontogramSelection => {
    const parts = str.split('|').map(p => p.trim());
    const teeth: string[] = [];
    const quadrants: string[] = [];
    const arcades: string[] = [];

    parts.forEach(part => {
      if (part.includes('SUPERIOR') || part.includes('INFERIOR')) {
        arcades.push(part);
      } else if (part.match(/^Q[1-4]$/)) {
        quadrants.push(part);
      } else {
        teeth.push(...part.split(',').map(t => t.trim()));
      }
    });

    return {
      mode: arcades.length > 0 ? 'ARCADE' : quadrants.length > 0 ? 'QUADRANT' : 'UNIT',
      teeth,
      quadrants,
      arcades,
      displayText: str,
    };
  };

  const handlePatientSelect = (patientId: string, _patientName: string) => {
    loadPatient(patientId);
    setShowPatientModal(false);
  };

  const handleAddMedicalAct = (act: BudgetMedicalActInfo) => {
    const newLine: EditableLine = {
      tempId: `new-${Date.now()}-${Math.random()}`,
      medicalActId: act.id,
      medicalActCode: act.code,
      medicalActName: act.name,
      requiresOdontogram: act.requiresOdontogram,
      odontogramType: act.odontogramType,
      quantity: 1,
      unitPrice: act.calculatedPrice,
      priceSource: act.priceSource,
      discountType: null,
      discountValue: null,
      discountId: null,
      discountName: null,
      odontogramSelection: null,
      societyId: act.societies.find(s => s.isActSpecific)?.id || act.societies.find(s => s.isDefault)?.id || defaultSocietyId,
      societyName: act.societies.find(s => s.isActSpecific)?.name || act.societies.find(s => s.isDefault)?.name || null,
      tariffId: null,
      tariffName: null,
      availableSocieties: act.societies,
    };

    setLines((prev) => [...prev, newLine]);

    // Si requiere odontograma, abrir el modal automáticamente
    if (act.requiresOdontogram) {
      setCurrentOdontogramLineId(newLine.tempId);
      setShowOdontogram(true);
    }

    setShowMedicalActSearch(false);
  };

  const handleLoadTemplate = (preview: TemplatePreview, replaceExisting: boolean) => {
    const newLines: EditableLine[] = preview.lines.map((line, index) => ({
      tempId: `template-${Date.now()}-${index}`,
      medicalActId: (line as any).medicalActId || '',
      medicalActCode: line.medicalActCode,
      medicalActName: line.medicalActName,
      requiresOdontogram: (line as any).requiresOdontogram || false,
      odontogramType: (line as any).odontogramType || null,
      quantity: line.quantity,
      unitPrice: line.unitPrice,
      priceSource: line.priceSource,
      discountType: null,
      discountValue: null,
      discountId: null,
      discountName: null,
      odontogramSelection: null,
      societyId: (line as any).societyId || null,
      societyName: line.societyName,
      tariffId: (line as any).tariffId || null,
      tariffName: line.tariffName,
      availableSocieties: (line as any).availableSocieties || [],
    }));

    // replaceExisting=true  → reemplazar líneas existentes
    // replaceExisting=false → añadir a las existentes (preserva las actuales)
    setLines(prev => replaceExisting ? newLines : [...prev.filter(l => !l._deleted), ...newLines]);
    setShowTemplateLoader(false);

    // Abrir odontograma para la primera línea que lo requiera
    const firstOdontogramLine = newLines.find(l => l.requiresOdontogram && !l.odontogramSelection);
    if (firstOdontogramLine) {
      setTimeout(() => {
        setCurrentOdontogramLineId(firstOdontogramLine.tempId);
        setShowOdontogram(true);
      }, 100);
    }
  };

  const handleUpdateLine = (tempId: string, updates: Partial<EditableLine>) => {
    setLines((prev) =>
      prev.map((line) => (line.tempId === tempId ? { ...line, ...updates } : line))
    );
  };

  const handleRemoveLine = (tempId: string) => {
    setLines((prev) =>
      prev.map((line) =>
        line.tempId === tempId ? { ...line, _deleted: true } : line
      )
    );
  };

  const handleDuplicateLine = (tempId: string) => {
    const lineToDuplicate = lines.find((l) => l.tempId === tempId);
    if (lineToDuplicate) {
      const newLine: EditableLine = {
        ...lineToDuplicate,
        tempId: `new-${Date.now()}-${Math.random()}`,
        odontogramSelection: null, // No duplicar selección de odontograma
      };
      setLines((prev) => [...prev, newLine]);
    }
  };

  const handleOpenOdontogram = (tempId: string) => {
    setCurrentOdontogramLineId(tempId);
    setShowOdontogram(true);
  };

  const handleOdontogramConfirm = (selection: OdontogramSelection) => {
    if (currentOdontogramLineId) {
      handleUpdateLine(currentOdontogramLineId, { odontogramSelection: selection });
      
      // Buscar la siguiente línea que requiera odontograma (después de actualizar)
      const updatedLines = lines.map(l => 
        l.tempId === currentOdontogramLineId 
          ? { ...l, odontogramSelection: selection }
          : l
      );
      
      const nextPendingLine = updatedLines.find(
        l => l.requiresOdontogram && !l.odontogramSelection && l.tempId !== currentOdontogramLineId
      );
      
      if (nextPendingLine) {
        // Hay más líneas pendientes, abrir el modal para la siguiente
        setTimeout(() => {
          setCurrentOdontogramLineId(nextPendingLine.tempId);
          setShowOdontogram(true);
        }, 200);
      } else {
        setShowOdontogram(false);
        setCurrentOdontogramLineId(null);
      }
    } else {
      setShowOdontogram(false);
      setCurrentOdontogramLineId(null);
    }
  };

  const calculateLineTotals = (line: EditableLine) => {
    const subtotal = line.quantity * line.unitPrice;
    let discountAmount = 0;
    
    if (line.discountType === 'PERCENTAGE' && line.discountValue) {
      discountAmount = subtotal * (line.discountValue / 100);
    } else if (line.discountType === 'FIXED' && line.discountValue) {
      discountAmount = line.discountValue;
    }
    
    // El total de una línea nunca puede ser negativo
    return { subtotal, discountAmount: Math.min(discountAmount, subtotal), total: Math.max(0, subtotal - discountAmount) };
  };

  // Líneas activas (excluyendo eliminadas)
  const activeLines = lines.filter(l => !l._deleted);
  const totals = {
    subtotal: activeLines.reduce((sum, line) => sum + calculateLineTotals(line).total, 0),
    globalDiscountAmount: 0,
    total: 0,
  };

  if (globalDiscountType === 'PERCENTAGE' && globalDiscountValue) {
    totals.globalDiscountAmount = totals.subtotal * (globalDiscountValue / 100);
  } else if (globalDiscountType === 'FIXED' && globalDiscountValue) {
    totals.globalDiscountAmount = globalDiscountValue;
  }
  // El total global nunca puede ser negativo
  totals.total = Math.max(0, totals.subtotal - totals.globalDiscountAmount);

  // Validar si hay líneas pendientes de odontograma
  const hasPendingOdontograms = activeLines.some(
    (line) => line.requiresOdontogram && !line.odontogramSelection
  );

  const handleSave = async (exitAfterSave: boolean = false) => {
    if (!patient) {
      setError('Debe seleccionar un paciente');
      return;
    }
    if (!treatment) {
      setShowTreatmentRequiredModal(true);
      return;
    }
    if (activeLines.length === 0) {
      setError('Debe añadir al menos un acto médico');
      return;
    }
    if (hasPendingOdontograms) {
      setError('Hay actos que requieren selección de odontograma');
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      const budgetLines = lines
        .filter((line) => !line._deleted || line.id) // Incluir líneas activas + eliminadas que existen en BD
        .map((line, index) => ({
          id: line.id,
          _delete: line._deleted ? true : undefined,
          medicalActId: line.medicalActId,
          quantity: line.quantity,
          societyId: line.societyId || undefined,
          tariffId: line.tariffId || undefined,
          priceOverride: line.priceSource === 'override' ? line.unitPrice : undefined,
          // null se envía explícitamente para que el backend borre el descuento.
          // undefined lo ignoraría y mantendría el valor anterior.
          discountType: line.discountType ?? null,
          discountValue: line.discountValue ?? null,
          toothNumbers: line.odontogramSelection?.displayText || undefined,
          sortOrder: line._deleted ? undefined : index,
        }));

      let savedBudget: BudgetResponse;

      if (budgetId) {
        // UPDATE - No enviar patientId
        const updateData = {
          treatmentId: treatment.id,
          internalNotes: internalNotes || undefined,
          publicNotes: publicNotes || undefined,
          discountType: globalDiscountType ?? null,
          discountValue: globalDiscountValue ?? null,
          lines: budgetLines,
        };
        savedBudget = await budgetsApi.update(budgetId, updateData);
      } else {
        // CREATE - Enviar todo
        const createData: CreateBudgetRequest = {
          patientId: patient.id,
          treatmentId: treatment.id,
          internalNotes: internalNotes || undefined,
          publicNotes: publicNotes || undefined,
          discountType: globalDiscountType ?? null,
          discountValue: globalDiscountValue ?? null,
          lines: budgetLines,
        };
        savedBudget = await budgetsApi.create(createData);
        // Guardar ID para siguientes guardados
        setBudgetId(savedBudget.id);
      }
      // Actualizar líneas locales con IDs del backend (las eliminadas ya no vienen)
      const activeLocalLines = lines.filter(l => !l._deleted);
      const updatedLines: EditableLine[] = savedBudget.lines.map((line) => {
        const localLine = activeLocalLines.find(l => l.id === line.id || l.medicalActId === line.medicalActId);
        return {
          id: line.id,
          tempId: localLine?.tempId || `saved-${line.id}`,
          medicalActId: line.medicalActId || '',
          medicalActCode: line.medicalActCode,
          medicalActName: line.medicalActName,
          requiresOdontogram: line.requiresOdontogram,
          odontogramType: line.odontogramType,
          quantity: line.quantity,
          unitPrice: line.unitPrice,
          priceSource: line.priceSource,
          discountType: line.discountType as 'PERCENTAGE' | 'FIXED' | null,
          discountValue: line.discountValue,
          // Preservar discountId/Name del estado local — el backend no los devuelve
          discountId: localLine?.discountId || null,
          discountName: localLine?.discountName || null,
          odontogramSelection: localLine?.odontogramSelection || null,
          societyId: line.society?.id || null,
          societyName: line.society?.name || null,
          tariffId: line.tariff?.id || null,
          tariffName: line.tariff?.name || null,
          availableSocieties: localLine?.availableSocieties || [],
        };
      });
      setLines(updatedLines);
      resetDirtyState();

      if (exitAfterSave) {
        navigate('/presupuestos');
      }
    } catch (err: any) {
      setError(err.message || 'Error al guardar el presupuesto');
    } finally {
      setIsSaving(false);
    }
  };

  // Obtener edad del paciente
  const patientAge = patient?.age || 30;

  // Obtener la línea actual para el modal de odontograma
  const currentOdontogramLine = currentOdontogramLineId
    ? lines.find((l) => l.tempId === currentOdontogramLineId)
    : null;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => safeNavigate('/presupuestos')}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isEditing ? `Editar Presupuesto ${budget?.budgetNumber}` : 'Nuevo Presupuesto'}
              {isDirty && (
                <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
                  Sin guardar
                </span>
              )}
            </h1>
            <p className="text-gray-500">
              {treatment ? treatment.name : 'Selecciona un tratamiento para nombrar el presupuesto'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
        {budgetId && (
          <button
            onClick={handleOpenSend}
            disabled={isSaving}
            className="inline-flex items-center px-3 py-2 lg:px-5 lg:py-2.5 text-sm lg:text-base bg-[#008DD0] text-white font-semibold rounded-xl hover:bg-[#0079b3] disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
          >
            <Send className="h-5 w-5 mr-2" />
            Enviar presupuesto
          </button>
        )}
        {budgetId && (
          <button
            onClick={() => setShowDeleteModal(true)}
            disabled={isSaving}
            className="inline-flex items-center px-3 py-2 text-sm font-semibold text-red-600 bg-white border border-red-200 rounded-xl hover:bg-red-50 disabled:opacity-50 transition-colors"
            title="Eliminar este borrador"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
        <button
          onClick={() => handleSave(false)}
          disabled={isSaving}
          className="inline-flex items-center px-3 py-2 lg:px-5 lg:py-2.5 text-sm lg:text-base bg-primary text-white font-semibold rounded-xl hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
        >
          {isSaving ? (
            <>
              <Loader2 className="h-5 w-5 mr-2 animate-spin" />
              {budgetId ? "Guardando..." : "Creando..."}
            </>
          ) : (
            <>
              <Save className="h-5 w-5 mr-2" />
              {budgetId ? "Guardar" : "Crear"}
            </>
          )}
        </button>
        <button
          onClick={() => handleSave(true)}
          disabled={isSaving}
          className="inline-flex items-center px-3 py-2 lg:px-5 lg:py-2.5 text-sm lg:text-base bg-green-600 text-white font-semibold rounded-xl hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
        >
          <Save className="h-5 w-5 mr-2" />
          {budgetId ? "Guardar y salir" : "Crear y salir"}
        </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
          <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium">Error</p>
            <p className="text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Banner: saldo a favor trasladado desde versión anterior */}
      {isEditing && budget && Number((budget as any).totalPaid) > 0 && (
        <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-xl">
          <CreditCard className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-blue-800">Pagos trasladados desde la versión anterior</p>
            <p className="text-sm text-blue-700 mt-0.5">
              El paciente ya ha realizado pagos por{' '}
              <strong>{formatCurrency(Number((budget as any).totalPaid))}</strong>.
              Este importe se descontará automáticamente de la deuda al aceptar el presupuesto.
            </p>
          </div>
        </div>
      )}

      {/* Grid principal */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 xl:gap-6">
        {/* Columna izquierda (2/3) */}
        <div className="xl:col-span-2 space-y-4 xl:space-y-6">
          {/* Fila compacta: Paciente + Tratamiento */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Paciente */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 xl:p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <User className="h-5 w-5 text-gray-400" />
                Paciente
              </h2>
              {patient && (
                <button
                  onClick={() => setShowPatientModal(true)}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  Cambiar paciente
                </button>
              )}
            </div>

            {patient ? (
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  {patient.firstName[0]}{patient.lastName[0]}
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-gray-900">
                    {patient.firstName} {patient.lastName}
                  </div>
                  <div className="text-sm text-gray-500">
                    {patient.documentType}: {patient.documentNumber}
                    {patient.age && <span className="ml-2">• {patient.age} años</span>}
                    {patient.age && patient.age < 14 && (
                      <span className="ml-2 px-2 py-0.5 bg-cyan-100 text-cyan-700 text-xs font-bold rounded-full">
                        Pediátrico
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowPatientModal(true)}
                className="w-full flex items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-blue-400 hover:text-blue-600 transition-colors"
              >
                <User className="h-5 w-5" />
                Seleccionar paciente
              </button>
            )}
          </div>

          {/* Tratamiento */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 xl:p-6">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <Tag className="h-5 w-5 text-gray-400" />
              Tratamiento (nombre del presupuesto)
            </h2>
            <TreatmentSelector
              value={treatment}
              onChange={setTreatment}
              siteId={activeSite?.id}
              placeholder="Seleccionar tratamiento..."
            />
          </div>
          </div>

          {/* Líneas del presupuesto */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 xl:p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <FileText className="h-5 w-5 text-gray-400" />
                Actos Médicos ({activeLines.length})
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowTemplateLoader(true)}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-purple-600 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors"
                >
                  <FileStack className="h-4 w-4 mr-1.5" />
                  Cargar plantilla
                </button>
                <button
                  onClick={() => setShowMedicalActSearch(true)}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Añadir acto
                </button>
              </div>
            </div>

            {/* Advertencia de odontogramas pendientes */}
            {hasPendingOdontograms && (
              <div className="mb-4 flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-sm">
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <span>
                  Hay <strong>{activeLines.filter(l => l.requiresOdontogram && !l.odontogramSelection).length}</strong> acto(s) 
                  que requieren selección de odontograma
                </span>
              </div>
            )}

            {activeLines.length > 0 ? (
              <div className="space-y-3">
                {activeLines.map((line, index) => (
                  <BudgetLineEditor
                    key={line.tempId}
                    line={line}
                    index={index}
                    onUpdate={(updates) => handleUpdateLine(line.tempId, updates)}
                    onRemove={() => handleRemoveLine(line.tempId)}
                    onDuplicate={() => handleDuplicateLine(line.tempId)}
                    onOpenOdontogram={() => handleOpenOdontogram(line.tempId)}
                    calculateTotals={() => calculateLineTotals(line)}
                    defaultSocietyId={defaultSocietyId}
                    patientAge={patientAge}
                    availableDiscounts={availableDiscounts}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-xl">
                <Calculator className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500 mb-2">No hay actos médicos en el presupuesto</p>
                <div className="flex items-center justify-center gap-3">
                  <button
                    onClick={() => setShowTemplateLoader(true)}
                    className="text-purple-600 hover:text-purple-700 text-sm font-medium"
                  >
                    Cargar plantilla
                  </button>
                  <span className="text-gray-300">o</span>
                  <button
                    onClick={() => setShowMedicalActSearch(true)}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                  >
                    Añadir acto manualmente
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Columna derecha (1/3) */}
        <div className="space-y-4 xl:space-y-6 xl:sticky xl:top-4">
          {/* Notas */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 xl:p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Notas</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Notas internas
                  <span className="text-gray-400 font-normal ml-1">(solo staff)</span>
                </label>
                <textarea
                  value={internalNotes}
                  onChange={(e) => setInternalNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none text-sm"
                  placeholder="Notas internas..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Notas públicas
                  <span className="text-gray-400 font-normal ml-1">(paciente)</span>
                </label>
                <textarea
                  value={publicNotes}
                  onChange={(e) => setPublicNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none text-sm"
                  placeholder="Info para el paciente..."
                />
              </div>
            </div>
          </div>

          {/* Resumen y totales */}
          {activeLines.length > 0 && (
            <BudgetSummary
              subtotal={totals.subtotal}
              globalDiscountType={globalDiscountType}
              globalDiscountValue={globalDiscountValue}
              globalDiscountAmount={totals.globalDiscountAmount}
              total={totals.total}
              globalDiscountId={globalDiscountId}
              globalDiscountName={globalDiscountName}
              availableDiscounts={availableDiscounts}
              onDiscountChange={(type, value, discountId, discountName) => {
                setGlobalDiscountType(type);
                setGlobalDiscountValue(value);
                setGlobalDiscountId(discountId);
                setGlobalDiscountName(discountName);
              }}
            />
          )}
        </div>
      </div>

      {/* Modal: Enviar presupuesto */}
      {showSendModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowSendModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <h3 className="text-base font-semibold text-gray-900">Enviar presupuesto</h3>
              <button onClick={() => setShowSendModal(false)} className="p-1.5 rounded-lg hover:bg-gray-100">
                <X className="h-4 w-4 text-gray-500" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-4">
              <p className="text-sm text-gray-600">
                El presupuesto pasará a estado <strong>Enviado</strong> y quedará bloqueado para edición.
              </p>
              <div className="space-y-3">
                <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                  <input type="radio" name="sendMode" checked={!sendEmail} onChange={() => setSendEmail(false)} className="text-[#008DD0]" />
                  <div>
                    <div className="flex items-center gap-2 font-medium text-sm text-gray-800">
                      <Printer className="h-4 w-4 text-gray-400" /> Solo marcar como enviado
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">El estado cambia pero no se envía email</p>
                  </div>
                </label>
                <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                  <input type="radio" name="sendMode" checked={sendEmail} onChange={() => setSendEmail(true)} className="text-[#008DD0]" />
                  <div>
                    <div className="flex items-center gap-2 font-medium text-sm text-gray-800">
                      <Mail className="h-4 w-4 text-gray-400" /> Enviar PDF por email
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">Se genera el PDF y se adjunta al correo del paciente</p>
                  </div>
                </label>
              </div>
              {sendEmail && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email del destinatario</label>
                  <input
                    type="email"
                    value={emailTo}
                    onChange={(e) => setEmailTo(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                    placeholder="email@paciente.com"
                  />
                </div>
              )}
              <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
                <button onClick={() => setShowSendModal(false)} disabled={isSending}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-50">
                  Cancelar
                </button>
                <button onClick={handleConfirmSend} disabled={isSending}
                  className="px-4 py-2 text-sm font-medium bg-[#008DD0] text-white rounded-xl hover:bg-[#0079b3] disabled:opacity-50 flex items-center gap-2">
                  {isSending && <Loader2 className="h-4 w-4 animate-spin" />}
                  {sendEmail ? 'Enviar email' : 'Marcar como enviado'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modales */}
      <PatientSelectModal
        isOpen={showPatientModal}
        onClose={() => setShowPatientModal(false)}
        onSelect={handlePatientSelect}
      />

      <MedicalActSearch
        isOpen={showMedicalActSearch}
        onClose={() => setShowMedicalActSearch(false)}
        onSelect={handleAddMedicalAct}
        defaultSocietyId={defaultSocietyId}
      />

      <TemplateLoaderModal
        isOpen={showTemplateLoader}
        onClose={() => setShowTemplateLoader(false)}
        onLoad={handleLoadTemplate}
        siteId={activeSite?.id || ''}
        hasExistingLines={activeLines.length > 0}
      />

      {currentOdontogramLine && (
        <OdontogramModal
          isOpen={showOdontogram}
          onClose={() => {
            setShowOdontogram(false);
            setCurrentOdontogramLineId(null);
          }}
          onConfirm={handleOdontogramConfirm}
          patientAge={patientAge}
          initialSelection={currentOdontogramLine.odontogramSelection}
          medicalActName={currentOdontogramLine.medicalActName}
          odontogramType={currentOdontogramLine.odontogramType as OdontogramType}
        />
      )}

      {/* Modal de tratamiento requerido */}
      {showTreatmentRequiredModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowTreatmentRequiredModal(false)} />
          <div className="relative bg-white rounded-xl shadow-xl p-6 max-w-md mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <AlertCircle className="h-5 w-5 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Tratamiento requerido</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Para crear el presupuesto se debe seleccionar un tratamiento.
            </p>
            <div className="flex justify-end">
              <button
                onClick={() => setShowTreatmentRequiredModal(false)}
                className="px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Eliminar presupuesto borrador */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowDeleteModal(false)} />
          <div className="relative bg-white rounded-xl shadow-xl p-6 max-w-md mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <Trash2 className="h-5 w-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Eliminar presupuesto</h3>
            </div>
            {budget?.parentBudgetId && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                <strong>Atención:</strong> Este presupuesto es una rectificación. Al eliminarlo,
                el presupuesto original seguirá disponible en su estado anterior.
              </div>
            )}
            <p className="text-gray-600 mb-6">
              ¿Seguro que quieres eliminar este borrador? Esta acción no se puede deshacer.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={isDeleting}
                className="px-4 py-2 text-gray-700 bg-gray-100 font-medium rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {isDeleting && <Loader2 className="h-4 w-4 animate-spin" />}
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de salida */}
      {showExitConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => {
            setShowExitConfirmModal(false);
            setPendingNavigation(null);
          }} />
          <div className="relative bg-white rounded-xl shadow-xl p-6 max-w-md mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <AlertCircle className="h-5 w-5 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Cambios sin guardar</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Tienes cambios sin guardar en el presupuesto. ¿Qué deseas hacer?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowExitConfirmModal(false);
                  setPendingNavigation(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 font-medium rounded-lg hover:bg-gray-200 transition-colors"
              >
                Seguir editando
              </button>
              <button
                onClick={() => {
                  setShowExitConfirmModal(false);
                  if (pendingNavigation) {
                    pendingNavigation();
                  }
                }}
                className="px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
              >
                Salir sin guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
